import {
	HubConnectionBuilder,
	LogLevel,
	HubConnectionState
} from '@microsoft/signalr'
import type { HubConnection } from '@microsoft/signalr'
import { useChatStore } from '@/stores/chatStore'
import { authService } from './authService'
import type { SignalREvents } from '@/types/SignalR'

const CONNECTION_MAX_RETRIES = 5

class SignalRService {
	private connection: HubConnection | null = null
	private connectionRetryCount: number = 0
	private startPromise: Promise<HubConnection> | null = null

	private buildConnection (): HubConnection {
		const connection = new HubConnectionBuilder()
			.withUrl(import.meta.env.VITE_SIGNALR_HUB_URL, {
				withCredentials: false,
				accessTokenFactory: async () => {
					const token = await authService.getAccessToken()
					return token ?? ''
				}
			})
			.withAutomaticReconnect([0, 2000, 10000, 30000])
			.configureLogging(LogLevel.Information)
			.build()

		connection.onreconnecting((err) => {
			console.warn('SignalR reconnecting', err)
		})

		connection.onreconnected(async (connId) => {
			console.info('SignalR reconnected, connectionId=', connId)

			const chatStore = useChatStore()

			if (!chatStore.currentChannelId) {
				return
			}

			console.info('Reconnecting channel...')
			await chatStore.enterChannel()
		})

		connection.onclose((err) => {
			console.warn('SignalR connection closed', err)
			this.startPromise = null
			// attempt restart after a delay
			this.handleConnectionClose()
		})

		return connection
	}

	async start (): Promise<HubConnection> {
		// If we're already starting or have a connection that's connected, wait for it
		if (this.startPromise) {
			return this.startPromise
		}

		if (
			this.connection &&
			this.connection.state === HubConnectionState.Connected
		) {
			return this.connection
		}

		this.startPromise = this._startConnection()
		return this.startPromise
	}

	private async _startConnection (): Promise<HubConnection> {
		if (!this.connection) {
			this.connection = this.buildConnection()
		}

		try {
			await this.connection.start()

			this.connectionRetryCount = 0

			return this.connection
		} catch (err) {
			this.connection = null
			this.startPromise = null

			if (this.connectionRetryCount < CONNECTION_MAX_RETRIES) {
				console.warn('SignalR start failed, retrying in 2s', err)
				// cleanup and retry
				await new Promise((res) => setTimeout(res, 2000))
				this.connectionRetryCount++
				return this.start()
			}

			await this.stop()

			throw new Error(
				`SignalR start failed after limit of ${CONNECTION_MAX_RETRIES} retries.`
			)
		}
	}

	private async handleConnectionClose (): Promise<void> {
		try {
			await new Promise((res) => setTimeout(res, 2000))
			await this.start()
		} catch (e) {
			console.error('Failed to restart SignalR', e)
		}
	}

	async stop (): Promise<void> {
		this.startPromise = null
		this.connectionRetryCount = 0

		if (!this.connection) return

		if (this.connection) {
			try {
				await this.connection.stop()
			} catch (err) {
				console.warn('SignalR stop failed', err)
			} finally {
				this.connection = null
			}
		}
	}

	/**
	 * Attach an event handler. Will ensure connection is started first.
	 */
	async on<K extends keyof SignalREvents> (
		event: K,
		handler: SignalREvents[K]
	): Promise<void> {
		const conn = await this.start()
		conn.on(event, handler)
	}

	/**
	 * Attach an event handler. Will ensure connection is started first.
	 */
	async off<K extends keyof SignalREvents> (
		event: K,
		handler?: SignalREvents[K]
	): Promise<void> {
		const conn = await this.start()

		if (handler) {
			conn.off(event, handler)
		} else {
			conn.off(event)
		}
	}

	/**
	 * Invoke a hub method. Ensures connection is started.
	 */
	async invoke<TResult> (
		methodName: string,
		...args: unknown[]
	): Promise<TResult> {
		const chatStore = useChatStore()
		const accessId = chatStore.currentChannelAccessId

		if (!accessId) {
			throw new Error('signalr invoke is missing accessId')
		}

		const conn = await this.start()
		return conn.invoke<TResult>(methodName, accessId, ...args)
	}
}

const signalRService = new SignalRService()
export default signalRService
